import React, { useMemo, useState } from 'react';
import { TreeSelect } from 'antd';

import TagBadge from './TagBadge';
import useTagSearch, { itemTagTitleFor } from '../../hooks/useTagSearch';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { foldSearchText } from '../../utils/strings';
import { normalizeId } from '../../utils/ids';
import { tagIdOf, tagOwnerKey, tagParentIdOf, tagPathLabel, treeOrder } from '../../utils/tags';

/**
 * THE TAG PICKER (docs/plans/tags.md §5.2): one antd TreeSelect for every
 * place a tag is chosen — the item form's and the read view's "+ Add tag",
 * the filter and condition builders' `tags` row, the quick filter's Tags
 * chip and the selection bar's Tags…. It WRITES IDS AND SHOWS NAMES.
 *
 *   Collection tags   the collection's vocabulary (and its parent's), as the
 *                     tree they are (three levels, ruled d04); a parent is a
 *                     tag in its own right and can be picked
 *   Private tags      mine, muted with the user glyph ("Only you see this");
 *                     left out where a query is shared (`privateTags={null}`)
 *   Item tags         never listed (a card's items can number thousands):
 *                     found by their item's title through tags/search, as
 *                     you type, grouped by card (ruled d13 — by id only)
 *
 * Two ways to use it:
 *   - as a VALUE (`value` + `onChange`): a builder row, a quick filter, the
 *     selection bar — single or `multiple`;
 *   - as an APPLIER (`onSelect(id, meta)` / `onDeselect(id, meta)` with
 *     `value` = what is applied, `hideSelected`): the item's picker, where
 *     the chips beside it already say what is on, so the box shows none.
 *
 * `meta` is `{ kind: 'collection' | 'private' | 'item', tag, result? }` —
 * `result` for an item tag, the search answer (its `collection`).
 * `disabledFor(tag, kind, result)` → a reason (the node is greyed and says
 * it), or null; `noteFor(tag, kind, result)` → a quiet note beside a
 * pickable node ("Only you see this" for an item tag that would go on
 * privately). `notFoundContent` replaces the empty list's words — where
 * there is nothing to pick at all and the caller knows what to do about it
 * (T7: "No restricted tags yet…" where only restricted tags may be picked).
 */

const GROUP_PREFIX = '__group:';
const HINT_VALUE = '__hint:items';
const fold = text => foldSearchText(`${text || ''}`).trim();

const searchTextOf = tag => [tag?.name, ...(Array.isArray(tag?.aliases) ? tag.aliases : []), tagPathLabel(tag)].filter(Boolean).join(' ');

function treeNodes(tags, { kind, disabledFor, noteFor, entries }) {
	const ordered = treeOrder(tags || []);
	const byId = new Map();
	const roots = [];
	ordered.forEach(tag => {
		const id = tagIdOf(tag);
		if (!id) return;
		const reason = typeof disabledFor === 'function' ? disabledFor(tag, kind) : null;
		const note = !reason && typeof noteFor === 'function' ? noteFor(tag, kind) : null;
		const badge = <TagBadge tag={tag} size="xs" />;
		const node = {
			value: id,
			key: id,
			title: (
				<span className="tag-select__option" data-tag-option={id}>
					{badge}
					{reason ? <span className="tag-select__reason">{reason}</span> : null}
					{note ? <span className="tag-select__note">{note}</span> : null}
				</span>
			),
			label: badge,
			searchText: searchTextOf(tag),
			disabled: Boolean(reason),
			children: [],
		};
		entries.set(id, { kind, tag });
		byId.set(id, node);
		const parent = tagParentIdOf(tag);
		if (parent && byId.has(parent)) byId.get(parent).children.push(node);
		else roots.push(node);
	});
	const prune = node => ({ ...node, children: node.children.length ? node.children.map(prune) : undefined });
	return roots.map(prune);
}

const groupNode = (key, title, children) => ({
	value: `${GROUP_PREFIX}${key}`,
	key: `${GROUP_PREFIX}${key}`,
	title: <span className="tag-select__group">{title}</span>,
	label: title,
	searchText: '',
	selectable: false,
	children,
});

/**
 * The tree the picker shows (exported for its tests): `{ treeData, entries }`,
 * `entries` mapping each pickable id to its `{ kind, tag, result? }`.
 */
export function tagTreeData({
	collectionTags = [],
	privateTags = null,
	itemResults = [],
	searching = false,
	itemTagsOn = true,
	selected = [],
	itemTitles = null,
	cardsById = null,
	disabledFor,
	noteFor,
	ownerLabel,
	collectionHeading = 'Collection tags',
	privateHeading = 'Private tags · Only you see this',
}) {
	const entries = new Map();
	const treeData = [];

	// Collection tags, one group per owner when the vocabulary holds a
	// parent's tags too (it lists the collection's own first).
	const owners = new Map();
	(collectionTags || []).forEach(tag => {
		const key = tagOwnerKey(tag) || 'c:';
		if (!owners.has(key)) owners.set(key, []);
		owners.get(key).push(tag);
	});
	[...owners.entries()].forEach(([key, tags], index) => {
		const heading = index === 0
			? collectionHeading
			: ((typeof ownerLabel === 'function' && ownerLabel(key.slice(2))) || 'Tags from the collection above');
		const children = treeNodes(tags, { kind: 'collection', disabledFor, noteFor, entries });
		if (children.length) treeData.push(groupNode(`collection:${key}`, heading, children));
	});

	if (Array.isArray(privateTags)) {
		const children = treeNodes(privateTags, { kind: 'private', disabledFor, noteFor, entries });
		if (children.length) treeData.push(groupNode('private', privateHeading, children));
	}

	if (itemTagsOn) {
		const byCard = new Map();
		(itemResults || []).forEach(result => {
			const tag = result?.tag;
			const id = tagIdOf(tag);
			if (!id || entries.has(id)) return;
			const cardId = normalizeId(tag.card_id) || '';
			if (!byCard.has(cardId)) byCard.set(cardId, []);
			byCard.get(cardId).push(result);
		});
		// A chosen item tag the search is not showing still needs a node, or
		// the box would print its id.
		idsOf(selected).forEach(id => {
			if (entries.has(id) || [...byCard.values()].some(list => list.some(result => tagIdOf(result.tag) === id))) return;
			const known = (itemTitles && (itemTitles instanceof Map ? itemTitles.get(id) : itemTitles[id])) || itemTagTitleFor(id);
			if (!known) return;
			const cardId = normalizeId(known.card_id) || '';
			if (!byCard.has(cardId)) byCard.set(cardId, []);
			byCard.get(cardId).push({ kind: 'item', tag: { _id: id, kind: 'item', title: known.title, card_id: known.card_id, item_id: known.item_id }, collection: known.collection_id ? { _id: known.collection_id } : null, remembered: true });
		});
		const children = [];
		[...byCard.entries()].forEach(([cardId, results]) => {
			const card = cardsById ? cardsById[cardId] : null;
			const nodes = results.map(result => {
				const tag = { ...result.tag, kind: 'item', collection_id: normalizeId(result.collection?._id) || result.tag.collection_id || null };
				const id = tagIdOf(tag);
				const reason = typeof disabledFor === 'function' ? disabledFor(tag, 'item', result) : null;
				const note = !reason && typeof noteFor === 'function' ? noteFor(tag, 'item', result) : null;
				const badge = (
					<TagBadge
						tag={tag}
						size="xs"
						resolveItemTag={t => ({ record: card || null, title: t.title || 'Untitled item', collection: t.collection_id || null })}
					/>
				);
				entries.set(id, { kind: 'item', tag, result });
				return {
					value: id,
					key: id,
					title: (
						<span className="tag-select__option" data-tag-option={id}>
							{badge}
							{reason ? <span className="tag-select__reason">{reason}</span> : null}
							{note ? <span className="tag-select__note">{note}</span> : null}
						</span>
					),
					label: badge,
					searchText: tag.title || '',
					// The API matched the title already; a remembered pick shows
					// only while nothing is being searched.
					alwaysShow: !result.remembered,
					disabled: Boolean(reason),
				};
			});
			if (!nodes.length) return;
			children.push(cardId && card ? groupNode(`card:${cardId}`, card.label?.plural || card.name || 'Items', nodes) : groupNode(`card:${cardId || 'none'}`, 'Items', nodes));
		});
		if (!searching && !children.length) {
			children.push({
				value: HINT_VALUE,
				key: HINT_VALUE,
				title: <span className="tag-select__hint">Type to find an item used as a tag</span>,
				label: '',
				searchText: '',
				selectable: false,
				disabled: true,
			});
		}
		if (children.length) treeData.push(groupNode('items', 'Item tags', children));
	}

	return { treeData, entries };
}

/** Every node key that has children — what "expanded" can name. */
function keysWithChildren(nodes, out = []) {
	(nodes || []).forEach(node => {
		if (Array.isArray(node.children) && node.children.length) {
			out.push(node.key);
			keysWithChildren(node.children, out);
		}
	});
	return out;
}

function idsOf(value) {
	if (Array.isArray(value)) return value.map(normalizeId).filter(Boolean);
	const one = normalizeId(value);
	return one ? [one] : [];
}

export default function TagSelect({
	value,
	onChange,
	onSelect,
	onDeselect,
	multiple = false,
	collectionTags = [],
	privateTags = null,
	itemTags = true,
	itemTitles = null,
	searchCollectionId = null,
	disabledFor,
	noteFor,
	ownerLabel,
	collectionHeading,
	privateHeading,
	placeholder = 'Choose a tag',
	notFoundContent,
	hideSelected = false,
	status,
	disabled = false,
	autoFocus = false,
	open,
	onOpenChange,
	onBlur,
	className = '',
	popupClassName = '',
	'aria-label': ariaLabel = 'Tag',
	size,
}) {
	const [search, setSearch] = useState('');
	const cards = useOptionalCardDefinitions();
	const cardsById = cards?.cardsById || null;
	const { itemTags: itemResults, loading: searchingItems } = useTagSearch(search, { enabled: itemTags, collectionId: searchCollectionId });
	const selected = useMemo(() => idsOf(value), [value]);

	const { treeData, entries } = useMemo(() => tagTreeData({
		collectionTags,
		privateTags,
		itemResults,
		searching: Boolean(search.trim()),
		itemTagsOn: itemTags,
		selected,
		itemTitles,
		cardsById,
		disabledFor,
		noteFor,
		ownerLabel,
		...(collectionHeading ? { collectionHeading } : {}),
		...(privateHeading ? { privateHeading } : {}),
	}), [collectionTags, privateTags, itemResults, search, itemTags, selected, itemTitles, cardsById, disabledFor, noteFor, ownerLabel, collectionHeading, privateHeading]);

	const metaOf = id => entries.get(normalizeId(id)) || { kind: 'unknown', tag: null };

	// Expansion is tracked as what the person COLLAPSED, so a group that
	// arrives later — the item tags a search found — opens on its own
	// (TreeSelect's own default expands only what it mounted with). A search
	// shows everything it matched.
	const expandable = useMemo(() => keysWithChildren(treeData), [treeData]);
	const [collapsed, setCollapsed] = useState(() => new Set());
	const expandedKeys = search.trim() ? expandable : expandable.filter(key => !collapsed.has(key));
	const handleExpand = keys => {
		if (search.trim()) return;
		const open = new Set(keys);
		setCollapsed(new Set(expandable.filter(key => !open.has(key))));
	};

	const handleChange = next => {
		if (typeof onChange !== 'function') return;
		const list = Array.isArray(next) ? next : (next === undefined || next === null ? [] : [next]);
		const clean = list.filter(id => typeof id === 'string' && !id.startsWith(GROUP_PREFIX) && id !== HINT_VALUE);
		onChange(multiple ? clean : (clean[0] ?? undefined), multiple ? clean.map(metaOf) : metaOf(clean[0]));
	};

	return (
		<TreeSelect
			className={['tag-select', hideSelected ? 'tag-select--applier' : '', className].filter(Boolean).join(' ')}
			classNames={{ popup: { root: ['tag-select__popup', popupClassName].filter(Boolean).join(' ') } }}
			treeData={treeData}
			value={multiple ? selected : (selected[0] ?? undefined)}
			onChange={handleChange}
			onSelect={typeof onSelect === 'function' ? (id => onSelect(normalizeId(id), metaOf(id))) : undefined}
			onDeselect={typeof onDeselect === 'function' ? (id => onDeselect(normalizeId(id), metaOf(id))) : undefined}
			multiple={multiple}
			showSearch
			searchValue={search}
			onSearch={setSearch}
			autoClearSearchValue={!hideSelected}
			filterTreeNode={(input, node) => Boolean(node?.alwaysShow) || (Boolean(node?.searchText) && fold(node.searchText).includes(fold(input)))}
			treeNodeLabelProp="label"
			treeExpandedKeys={expandedKeys}
			onTreeExpand={handleExpand}
			tagRender={hideSelected ? () => <span className="tag-select__hidden-tag" /> : undefined}
			placeholder={placeholder}
			notFoundContent={searchingItems ? 'Searching…' : (notFoundContent && !search.trim() ? notFoundContent : 'No tag by that name')}
			status={status}
			disabled={disabled}
			autoFocus={autoFocus}
			open={open}
			onOpenChange={onOpenChange}
			onBlur={onBlur}
			allowClear={!hideSelected}
			popupMatchSelectWidth={false}
			virtual={false}
			size={size}
			aria-label={ariaLabel}
		/>
	);
}
